import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Empty, Loading } from "components/ui";
import Heading from "components/ui/Heading/Heading";
import ErrorView from "components/ui/ErrorView/ErrorView";
import Messages from "components/dashboard/AgentInbox/AgentInboxTickets/ChatTickets/ChatModule/LiveChat/MessageBody/Messages/Messages";
import { ticketsPhases } from "components/dashboard/AgentInbox/AgentInboxTickets/ChatTickets/common/TicketStatus/enum";
import { apiRoute } from "services";
import API from "services/lib/api";
import { dataQueryStatus } from "utils";
import { getErrorMessage, SmartTime } from "utils/helper";
import {
    AUTOMATION_ACTION_LABELS,
    TICKET_PHASE_LABELS,
} from "../enum";
import AutomationMatchingTicketsHeader from "./AutomationMatchingTicketsHeader/AutomationMatchingTicketsHeader";
import "./AutomationMatchingTickets.scss";

const { IDLE, LOADING, ERROR, DATAMODE, NULLMODE } = dataQueryStatus;

const formatDateTime = (value) => (value ? SmartTime(value) : "—");

const getCustomerLabel = (ticket) =>
    ticket?.customer?.email ||
    ticket?.customer?.firstName ||
    ticket?.ticketId ||
    "—";

const getAgentLabel = (ticket) => {
    if (!ticket?.assigned) {
        return "Unassigned";
    }
    if (ticket?.agent?.firstName || ticket?.agent?.email) {
        return ticket.agent.firstName || ticket.agent.email;
    }
    if (ticket?.assignedTeamMemberId) {
        return ticket.assignedTeamMemberId;
    }
    return "Assigned";
};

const PhaseBadge = ({ phase }) => {
    const meta = ticketsPhases[phase];

    if (!meta) {
        return (
            <span className='automation-matching-tickets__phase-fallback'>
                {TICKET_PHASE_LABELS[phase] || phase || "—"}
            </span>
        );
    }

    return (
        <div className='ticket__status automation-matching-tickets__phase'>
            <div
                className='status__circle'
                style={{ background: meta.fillColor }}
            />
            <span>{meta.title}</span>
        </div>
    );
};

const RunStatusBadge = ({ status }) => {
    if (!status) {
        return null;
    }

    const tone =
        status === "PENDING"
            ? "pending"
            : status === "APPLIED"
              ? "applied"
              : "default";

    return (
        <span
            className={`automation-matching-tickets__run-badge automation-matching-tickets__run-badge--${tone}`}>
            {status}
        </span>
    );
};

const BoolPill = ({ value, yesLabel = "Yes", noLabel = "No" }) => (
    <span
        className={`automation-matching-tickets__pill ${
            value ? "is-yes" : "is-no"
        }`}>
        {value ? yesLabel : noLabel}
    </span>
);

const AutomationMatchingTickets = () => {
    const { automationId } = useParams();
    const [status, setStatus] = useState(IDLE);
    const [errorMssg, setErrorMssg] = useState();
    const [automationName, setAutomationName] = useState("");
    const [actionType, setActionType] = useState("");
    const [tickets, setTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [messagesStatus, setMessagesStatus] = useState(IDLE);
    const [messagesError, setMessagesError] = useState();
    const [messages, setMessages] = useState([]);

    const getMatchingTickets = async () => {
        try {
            setErrorMssg();
            setStatus(LOADING);
            const res = await API.get(
                apiRoute.getAutomationMatchingTickets(automationId),
                { params: { limit: 50 } }
            );

            if (res.status === 200) {
                const payload = res?.data?.data || {};
                const list = payload.tickets || [];
                setAutomationName(payload.automationName || "");
                setActionType(payload.actionType || "");
                setTickets(list);
                setStatus(list.length > 0 ? DATAMODE : NULLMODE);
                setSelectedTicket(list.length > 0 ? list[0] : null);
            }
        } catch (err) {
            setErrorMssg(getErrorMessage(err));
            setStatus(ERROR);
        }
    };

    const getTicketMessages = async (ticketId) => {
        try {
            setMessagesError();
            setMessagesStatus(LOADING);
            setMessages([]);
            const res = await API.get(apiRoute.getTicketMessages(ticketId));

            if (res.status === 200) {
                const list = res?.data?.data || [];
                setMessages(list);
                setMessagesStatus(list.length > 0 ? DATAMODE : NULLMODE);
            }
        } catch (err) {
            setMessagesError(getErrorMessage(err));
            setMessagesStatus(ERROR);
        }
    };

    useEffect(() => {
        if (automationId) {
            getMatchingTickets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [automationId]);

    useEffect(() => {
        if (selectedTicket?.ticketId) {
            getTicketMessages(selectedTicket.ticketId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTicket?.ticketId]);

    const actionLabel = AUTOMATION_ACTION_LABELS[actionType] || actionType;

    const renderTicketList = () => {
        switch (status) {
            case LOADING:
                return (
                    <div className='automation-matching-tickets__state'>
                        <Loading />
                    </div>
                );
            case ERROR:
                return (
                    <div className='automation-matching-tickets__state'>
                        <ErrorView
                            message={errorMssg}
                            handleRetry={getMatchingTickets}
                        />
                    </div>
                );
            case NULLMODE:
                return (
                    <div className='automation-matching-tickets__state'>
                        <Empty text='No matching tickets right now' />
                    </div>
                );
            case DATAMODE:
                return (
                    <ul className='automation-matching-tickets__list'>
                        {tickets.map((ticket) => {
                            const isActive =
                                selectedTicket?.ticketId === ticket.ticketId;

                            return (
                                <li key={ticket.ticketId}>
                                    <button
                                        type='button'
                                        className={`automation-matching-tickets__item ${
                                            isActive ? "is-active" : ""
                                        }`}
                                        onClick={() =>
                                            setSelectedTicket(ticket)
                                        }>
                                        <div className='automation-matching-tickets__item-top'>
                                            <div>
                                                <p className='automation-matching-tickets__item-title'>
                                                    {getCustomerLabel(ticket)}
                                                </p>
                                                <p className='automation-matching-tickets__item-id'>
                                                    {ticket.ticketId}
                                                </p>
                                            </div>
                                            <RunStatusBadge
                                                status={ticket.runStatus}
                                            />
                                        </div>

                                        <div className='automation-matching-tickets__item-badges'>
                                            <PhaseBadge
                                                phase={ticket.ticketPhase}
                                            />
                                        </div>

                                        <div className='automation-matching-tickets__item-footer'>
                                            <span className='automation-matching-tickets__item-time'>
                                                Updated{" "}
                                                {formatDateTime(
                                                    ticket.updatedDate
                                                )}
                                            </span>
                                            <div className='automation-matching-tickets__item-pills'>
                                                <BoolPill
                                                    value={ticket.ticketReplied}
                                                    yesLabel='Replied'
                                                    noLabel='No reply'
                                                />
                                                <span
                                                    className={`automation-matching-tickets__assign ${
                                                        ticket.assigned
                                                            ? "is-assigned"
                                                            : ""
                                                    }`}>
                                                    {ticket.assigned
                                                        ? getAgentLabel(ticket)
                                                        : "Unassigned"}
                                                </span>
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                );
            default:
                return null;
        }
    };

    const renderDetailMeta = () => {
        if (!selectedTicket) {
            return null;
        }

        const rows = [
            {
                label: "Phase",
                value: (
                    <PhaseBadge phase={selectedTicket.ticketPhase} />
                ),
            },
            {
                label: "Created",
                value: formatDateTime(selectedTicket.createdDate),
            },
            {
                label: "Last updated",
                value: formatDateTime(selectedTicket.updatedDate),
            },
            {
                label: "Agent replied",
                value: (
                    <BoolPill value={selectedTicket.ticketReplied} />
                ),
            },
            {
                label: "Assigned",
                value: getAgentLabel(selectedTicket),
            },
            ...(selectedTicket.agentFirstRespondedAt
                ? [
                      {
                          label: "First agent reply",
                          value: formatDateTime(
                              selectedTicket.agentFirstRespondedAt
                          ),
                      },
                  ]
                : []),
            ...(selectedTicket.waitingOnCustomerSince
                ? [
                      {
                          label: "Waiting since",
                          value: formatDateTime(
                              selectedTicket.waitingOnCustomerSince
                          ),
                      },
                  ]
                : []),
            ...(selectedTicket.runStatus
                ? [
                      {
                          label: "Automation run",
                          value: (
                              <RunStatusBadge
                                  status={selectedTicket.runStatus}
                              />
                          ),
                      },
                  ]
                : []),
        ];

        return (
            <div className='automation-matching-tickets__detail-meta'>
                {rows.map((row) => (
                    <div
                        key={row.label}
                        className='automation-matching-tickets__detail-meta-item'>
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                    </div>
                ))}
            </div>
        );
    };

    const renderMessages = () => {
        if (!selectedTicket) {
            return (
                <div className='automation-matching-tickets__state'>
                    <Empty text='Select a ticket to preview its conversation' />
                </div>
            );
        }

        switch (messagesStatus) {
            case LOADING:
                return (
                    <div className='automation-matching-tickets__state'>
                        <Loading />
                    </div>
                );
            case ERROR:
                return (
                    <div className='automation-matching-tickets__state'>
                        <ErrorView
                            message={messagesError}
                            handleRetry={() =>
                                getTicketMessages(selectedTicket.ticketId)
                            }
                        />
                    </div>
                );
            case NULLMODE:
                return (
                    <div className='automation-matching-tickets__state'>
                        <Empty text='No messages on this ticket yet' />
                    </div>
                );
            case DATAMODE:
                return (
                    <div className='automation-matching-tickets__messages'>
                        <Messages
                            customer={selectedTicket.customer}
                            defaultMessages={messages}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <AutomationMatchingTicketsHeader
                automationId={automationId}
                automationName={automationName}
            />
            <div className='padded__layout pt-4 automation-matching-tickets'>
                <div className='automation-matching-tickets__intro'>
                    <Heading
                        headingText='Matching tickets'
                        headingSubText={
                            actionLabel
                                ? `Open tickets that currently match “${automationName || "this automation"}” (${actionLabel}).`
                                : "Open tickets that currently match this automation’s conditions."
                        }
                    />
                    {status === DATAMODE && (
                        <span className='automation-matching-tickets__count'>
                            {tickets.length} ticket
                            {tickets.length === 1 ? "" : "s"}
                        </span>
                    )}
                </div>

                <div className='automation-matching-tickets__layout'>
                    <aside className='automation-matching-tickets__sidebar'>
                        <div className='automation-matching-tickets__sidebar-head'>
                            <h3>Tickets</h3>
                            {status === DATAMODE && (
                                <span>{tickets.length}</span>
                            )}
                        </div>
                        {renderTicketList()}
                    </aside>

                    <section className='automation-matching-tickets__panel'>
                        {selectedTicket && (
                            <div className='automation-matching-tickets__panel-head'>
                                <div>
                                    <h3>
                                        {getCustomerLabel(selectedTicket)}
                                    </h3>
                                    <p>{selectedTicket.ticketId}</p>
                                </div>
                                <PhaseBadge
                                    phase={selectedTicket.ticketPhase}
                                />
                            </div>
                        )}

                        {renderDetailMeta()}

                        <div className='automation-matching-tickets__thread'>
                            <div className='automation-matching-tickets__thread-label'>
                                Conversation
                            </div>
                            {renderMessages()}
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default AutomationMatchingTickets;
